import React from 'react';

import { SEVERITIES } from '@totem/components/services/threats/eventsDashboard/report/severity';
import {
  DayCount,
  SeverityCounts,
  TypeCount,
} from '@totem/components/services/threats/eventsDashboard/report/types';

import './eventsReport.css';

const TREND_SEVERITIES = SEVERITIES.filter((severity) => severity.value >= 3);

const DONUT_COLORS = [
  '#E8540A',
  '#F97316',
  '#F59E0B',
  '#122048',
  '#3B82F6',
  '#1A2D5A',
  '#64748B',
  '#94A3B8',
];
const DONUT_OTHER_COLOR = '#CBD5E1';
const DONUT_SLICES = 7;

// Stacked daily bars of Critical, Major and Minor events; lower severities are counted but not drawn.
export const SeverityTrendChart = ({ days }: { days: DayCount[] }) => {
  const width = 900;
  const height = 220;
  const left = 36;
  const bottom = 24;
  const plotHeight = height - bottom - 8;
  const totals = days.map((day) =>
    TREND_SEVERITIES.reduce((sum, severity) => sum + day[severity.key], 0),
  );
  const max = Math.max(1, ...totals);
  const slot = (width - left) / Math.max(1, days.length);
  const barWidth = Math.max(2, slot * 0.7);
  // Small maximums round several shares to the same value; draw each gridline once.
  const ticks = Array.from(
    new Set([0, 0.25, 0.5, 0.75, 1].map((share) => Math.round(max * share))),
  );
  const labelIndexes = [
    0,
    Math.floor(days.length / 4),
    Math.floor(days.length / 2),
    Math.floor((3 * days.length) / 4),
    days.length - 1,
  ];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      styleName="trend-chart"
      role="img"
      aria-label="Events per day by severity"
    >
      {ticks.map((tick) => {
        const y = 8 + plotHeight - (tick / max) * plotHeight;
        return (
          <g key={`tick-${tick}`}>
            <line x1={left} x2={width} y1={y} y2={y} styleName="grid-line" />
            <text
              x={left - 6}
              y={y + 3}
              textAnchor="end"
              styleName="axis-label"
            >
              {tick}
            </text>
          </g>
        );
      })}
      {days.map((day, idx) => {
        let y = 8 + plotHeight;
        return (
          <g key={day.day}>
            {TREND_SEVERITIES.slice()
              .reverse()
              .map((severity) => {
                const value = day[severity.key];
                const barHeight = (value / max) * plotHeight;
                y -= barHeight;
                return value > 0 ? (
                  <rect
                    key={severity.key}
                    x={left + idx * slot + (slot - barWidth) / 2}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    fill={severity.color}
                  >
                    <title>{`${day.day} ${severity.label}: ${value}`}</title>
                  </rect>
                ) : null;
              })}
            {labelIndexes.includes(idx) && (
              <text
                x={left + idx * slot + slot / 2}
                y={height - 6}
                textAnchor="middle"
                styleName="axis-label"
              >
                {day.day.slice(5)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export const SeverityLegend = () => (
  <div styleName="legend">
    {TREND_SEVERITIES.map((severity) => (
      <span key={severity.key}>
        <span
          styleName="legend-swatch"
          style={{ background: severity.color }}
        />
        {severity.label}
      </span>
    ))}
  </div>
);

export const IssueTypesDonut = ({
  types,
  total,
}: {
  types: TypeCount[];
  total: number;
}) => {
  if (total === 0) {
    return <div styleName="empty">No events</div>;
  }

  const slices = types.slice(0, DONUT_SLICES).map((type, idx) => ({
    label: type.eventType || 'Not recorded',
    count: type.count,
    color: DONUT_COLORS[idx],
  }));
  const shown = slices.reduce((sum, slice) => sum + slice.count, 0);
  if (total > shown) {
    slices.push({
      label: `Other (${Math.max(0, types.length - DONUT_SLICES)}+)`,
      count: total - shown,
      color: DONUT_OTHER_COLOR,
    });
  }

  const radius = 64;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div styleName="donut">
      <svg
        viewBox="0 0 150 150"
        styleName="donut-chart"
        role="img"
        aria-label="Events by type"
      >
        {slices.map((slice) => {
          const length = (slice.count / total) * circumference;
          const circle = (
            <circle
              key={slice.label}
              cx={75}
              cy={75}
              r={radius}
              fill="none"
              stroke={slice.color}
              strokeWidth={22}
              strokeDasharray={`${length} ${circumference - length}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 75 75)"
            >
              <title>{`${slice.label}: ${slice.count} (${((slice.count / total) * 100).toFixed(1)}%)`}</title>
            </circle>
          );
          offset += length;
          return circle;
        })}
        <text x={75} y={73} textAnchor="middle" styleName="donut-total">
          {total.toLocaleString()}
        </text>
        <text x={75} y={90} textAnchor="middle" styleName="donut-caption">
          events
        </text>
      </svg>
      <div styleName="donut-legend">
        {slices.map((slice) => (
          <div key={slice.label} styleName="donut-legend-row">
            <span
              styleName="legend-swatch"
              style={{ background: slice.color }}
            />
            <span styleName="donut-legend-label" title={slice.label}>
              {slice.label}
            </span>
            <span styleName="donut-legend-count">
              {slice.count.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Trend across the report window; red when the second half is busier than the first, green when quieter.
// Events processed (first seen) and how many of them are still open, over the same buckets.
export const ProcessedTrend = ({
  processed,
  open,
  width = 90,
  height = 22,
}: {
  processed: number[];
  open: number[];
  width?: number;
  height?: number;
}) => {
  const max = Math.max(...processed, ...open, 0);
  if (max === 0) {
    return <span styleName="sub">—</span>;
  }

  const line = (values: number[]) =>
    values
      .map(
        (value, idx) =>
          `${(idx / Math.max(1, values.length - 1)) * (width - 4) + 2},${height - 2 - (value / max) * (height - 4)}`,
      )
      .join(' ');
  const sum = (values: number[]) =>
    values.reduce((total, value) => total + value, 0);

  return (
    <svg
      width={width}
      height={height}
      styleName="trend"
      role="img"
      aria-label={`${sum(processed).toLocaleString()} events processed, ${sum(open).toLocaleString()} still open`}
    >
      <title>
        {`${sum(processed).toLocaleString()} processed · ${sum(open).toLocaleString()} still open`}
      </title>
      <polyline styleName="trend-processed" points={line(processed)} />
      <polyline styleName="trend-open" points={line(open)} />
    </svg>
  );
};

export const TrendLegend = () => (
  <span styleName="trend-legend">
    <i styleName="trend-key trend-key-processed" />
    Processed
    <i styleName="trend-key trend-key-open" />
    Still open
  </span>
);

export const SeverityMixBar = ({
  counts,
  total,
  scale = 1,
}: {
  counts: SeverityCounts;
  total: number;
  scale?: number;
}) => (
  <div styleName="mix-bar" style={{ width: `${Math.max(2, scale * 100)}%` }}>
    {total > 0 &&
      SEVERITIES.map((severity) =>
        counts[severity.key] > 0 ? (
          <i
            key={severity.key}
            style={{
              width: `${(counts[severity.key] / total) * 100}%`,
              background: severity.color,
            }}
            title={`${severity.label}: ${counts[severity.key]}`}
          />
        ) : null,
      )}
  </div>
);
